import React, { useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { Modal, ModalTitle, ModalBody, ModalFooter } from 'design-system/src/components/Modal';
import Button from 'design-system/src/components/Button/Button';
import { Typography } from 'design-system/src/components/Typography/Typography';
import { ErrorMessage } from 'design-system/src/components/ErrorMessage/ErrorMessage';
import { Toast, StandardErrorToast } from 'design-system/src/components/Toast/Toast';
import { emptyTableCellCopy } from 'design-system/src/sharedCopy';
import { Input } from '../Input';
import { type ParsedAddress } from '../AddressAutocomplete';
import { useGeocode } from '../AddressAutocomplete/useGeocode';
import { AddressSearchSection, ManualAddressFields } from '../onboarding/onboardingStepModals/AddOfficesModal';
import { client } from '../../api';
import * as Sentry from '@sentry/react';
import { type OfficeDetail } from './types';

type AddAddressToOfficeModalProps = {
  /** Called when the modal should close. */
  onClose: () => void;
  /** The office to add an address to. */
  office: OfficeDetail;
};

/**
 * Includes name/tin for structural compatibility with ManualAddressFields,
 * which is typed against AddOfficesModal's FormData.
 */
type FormData = {
  name: string;
  tin: string;
  formatted_address: string;
  latitude: number | null;
  longitude: number | null;
  address_line_2: string;
  address_line_1: string;
  city: string;
  state: string;
  zipcode: string;
};

const AddAddressToOfficeModal: React.FC<AddAddressToOfficeModalProps> = ({ onClose, office }) => {
  const queryClient = useQueryClient();
  const { geocode } = useGeocode();
  const [isManualEntry, setIsManualEntry] = useState(false);
  const [addressInputValue, setAddressInputValue] = useState('');
  const [addressError, setAddressError] = useState<string | null>(null);

  const getDefaults = (): FormData => ({
    name: office.name,
    tin: office.tin ?? '',
    formatted_address: '',
    latitude: null,
    longitude: null,
    address_line_2: '',
    address_line_1: '',
    city: '',
    state: '',
    zipcode: '',
  });

  const {
    control,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: getDefaults(),
  });

  const { mutate: createOffice, isPending } = useMutation({
    mutationFn: (data: { name: string; tin: string | null; address: string; latitude: number; longitude: number }) =>
      client.post('/settings/offices', data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['offices-v2'] });
      queryClient.invalidateQueries({ queryKey: ['unmatched-addresses'] });
      toast.custom(() => <Toast variant="success">Address added</Toast>);
      onClose();
    },
    onError: (error: any) => {
      if (error?.response?.status === 409) {
        toast.custom(() => <Toast variant="error">An office with this address already exists</Toast>);
      } else {
        Sentry.captureException(error);
        toast.custom(() => <StandardErrorToast />);
      }
    },
  });

  const handleAddressSelect = (address: ParsedAddress) => {
    setValue('formatted_address', address.formatted_address);
    setValue('latitude', address.latitude ?? null);
    setValue('longitude', address.longitude ?? null);
    setValue('address_line_1', address.address_line_1);
    setValue('address_line_2', address.address_line_2);
    setValue('city', address.city);
    setValue('state', address.state);
    setValue('zipcode', address.zipcode);
    setAddressInputValue(address.formatted_address);
    setAddressError(null);
  };

  const onSubmit = async (data: FormData) => {
    let address = data.formatted_address;
    let lat = data.latitude;
    let lng = data.longitude;

    if (isManualEntry) {
      address = `${data.address_line_1}, ${data.city}, ${data.state} ${data.zipcode}`;
      const parsed = await geocode(address);
      if (parsed) {
        lat = parsed.latitude ?? null;
        lng = parsed.longitude ?? null;
        address = parsed.formatted_address;
      } else {
        setAddressError('Could not verify address. Please try searching for it instead.');
        return;
      }
    }

    const trimmedAddress = address.trim();

    if (!trimmedAddress) {
      setAddressError('Please select an address.');
      return;
    }

    if (lat == null || lng == null) {
      setAddressError('Could not determine address location. Please try again.');
      return;
    }

    const addressLine2 = data.address_line_2.trim();
    const fullAddress = addressLine2 ? `${trimmedAddress}, ${addressLine2}` : trimmedAddress;

    createOffice({
      name: office.name,
      tin: office.tin,
      address: fullAddress,
      latitude: lat,
      longitude: lng,
    });
  };

  const watchedFormattedAddress = watch('formatted_address');
  const watchedAddressLine1 = watch('address_line_1');
  const watchedCity = watch('city');
  const watchedState = watch('state');
  const watchedZipcode = watch('zipcode');

  const hasRequiredFields = isManualEntry
    ? watchedAddressLine1?.trim() && watchedCity?.trim() && watchedState?.trim() && watchedZipcode?.trim()
    : watchedFormattedAddress?.trim();

  return (
    <Modal onClose={onClose}>
      <ModalTitle>Add an address</ModalTitle>
      <ModalBody>
        <div className="flex flex-col gap-0.5">
          <Typography styledAs="bodySmallDMSans" weight={700}>
            {office.name}
          </Typography>
          <Typography styledAs="bodySmallDMSans">{office.address}</Typography>
          <Typography styledAs="bodySmallDMSans" color="var(--grayscale-600)">
            TIN: {office.tin || emptyTableCellCopy}
          </Typography>
        </div>

        <div className="h-px w-full bg-grayscale-100" />

        <div className="flex flex-col gap-3">
          <Typography styledAs="bodySmallDMSans" weight={700}>
            Office address
          </Typography>

          <div className="flex flex-col gap-3">
            {isManualEntry ? (
              <>
                <ManualAddressFields
                  control={control}
                  errors={errors}
                  onAddressLookup={() => setIsManualEntry(false)}
                />
                {addressError && <ErrorMessage>{addressError}</ErrorMessage>}
              </>
            ) : (
              <>
                <AddressSearchSection
                  addressInputValue={addressInputValue}
                  addressError={addressError}
                  onAddressInputChange={(val) => {
                    setAddressInputValue(val);
                    setAddressError(null);
                    if (!val.trim()) {
                      setValue('formatted_address', '');
                      setValue('latitude', null);
                      setValue('longitude', null);
                    }
                  }}
                  onAddressSelect={handleAddressSelect}
                  onManualEntry={() => setIsManualEntry(true)}
                />

                <Controller
                  control={control}
                  name="address_line_2"
                  render={({ field }) => (
                    <Input
                      label={
                        <span>
                          Address Line 2 <span className="text-grayscale-500">Optional</span>
                        </span>
                      }
                      placeholder="Apt / Suite / Floor"
                      {...field}
                    />
                  )}
                />
              </>
            )}
          </div>
        </div>
      </ModalBody>
      <ModalFooter>
        <Button variant="tertiary" disabled={isPending} onClick={onClose}>
          Back
        </Button>
        <Button variant="primary" disabled={!hasRequiredFields} isLoading={isPending} onClick={handleSubmit(onSubmit)}>
          Save
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default AddAddressToOfficeModal;
